import { type MarketingLocale, useLocalized } from '~/lib/marketing-locale'
import { SparkleIcon } from './marketing-glyphs'
import { ScrollReveal } from './scroll-reveal'
import { TruchetField } from './truchet-field'

/*
 * AI — the assistant and the SketchUp-to-render flow. Hero chat on top, a
 * floating render comparison overlapping it on the right, then a wide bento
 * of "abilities" each with its own working UI surface, not just text.
 *
 * Copy is locale-keyed — see the note in `crm-section.tsx`.
 */

type Bubble = { mine: boolean; text: string }
type CheckRow = { icon: string; label: string; tone: string }
type Stat = { l: string; v: string }
type AbilityCopy = { tag: string; title: string; body: string }

type AiContent = {
  eyebrow: string
  heading: string
  intro: string
  assistant: {
    name: string
    context: string
    sources: string
    bubbles: readonly Bubble[]
    draftLabel: string
    addLabel: string
    editLabel: string
    meta: string
  }
  render: {
    title: string
    version: string
    beforeLabel: string
    speedBadge: string
    stats: readonly { label: string; value: string }[]
  }
  abilitiesIntro: string
  summary: AbilityCopy & { miniLabel: string; stats: readonly Stat[] }
  contract: AbilityCopy & { rows: readonly CheckRow[]; note: string }
  proposal: AbilityCopy & {
    sourceLabel: string
    suggestedLabel: string
    value: string
    terms: string
  }
  renderAbility: { tag: string; title: string; body: string; chips: readonly string[] }
}

const CONTENT: Record<MarketingLocale, AiContent> = {
  en: {
    abilitiesIntro: 'Four abilities that change the rhythm of the practice',
    assistant: {
      addLabel: 'Add to project',
      bubbles: [
        { mine: true, text: 'Summarize what moved on Whitfield House this week.' },
        {
          mine: false,
          text: 'Maya closed out the millwork detailing, the plumbing set was coordinated, and electrical approval is still outstanding. Construction is at 64% of scope delivered.',
        },
        { mine: true, text: 'Draft the sign-off letter for this phase.' },
      ],
      context: 'With context from Whitfield House',
      draftLabel: '✦ PHASE SIGN-OFF, DRAFT',
      editLabel: 'Edit',
      meta: '132 words · 3 sources',
      name: 'Planno AI',
      sources: '7 project sources',
    },
    contract: {
      body: 'Run a contract, amendment, or sign-off against the practice’s legal checklist. It flags what needs another look.',
      note: '✦ 2 items need review',
      rows: [
        { icon: '✓', label: 'Termination clause', tone: '#7bb86b' },
        { icon: '✓', label: 'Governing law · venue', tone: '#7bb86b' },
        { icon: '!', label: 'Intellectual property', tone: '#e8a13d' },
        { icon: '✕', label: 'Schedule + payment', tone: '#e0593f' },
      ],
      tag: 'REVIEW',
      title: 'Reads contracts against your own bar',
    },
    eyebrow: 'INTELLIGENCE',
    heading: 'AI that has already read everything on your project',
    intro:
      'The assistant knows the files, the conversations, the budget, and the schedule. Ask a question, request a draft, or generate a rendering — all inside the same project.',
    proposal: {
      body: 'A completed brief becomes a proposal with scope, phases, payment schedule, and fee, ready for you to review.',
      sourceLabel: 'Brief, Marion Vaughan',
      suggestedLabel: 'SUGGESTED PROPOSAL',
      tag: 'GENERATION',
      terms: '5 phases · 6 × $6,400',
      title: 'Brief becomes proposal',
      value: '$48,000',
    },
    render: {
      beforeLabel: 'BEFORE · SKETCHUP',
      speedBadge: '2.8s · free',
      stats: [
        { label: 'Time', value: 'Day · 2 PM' },
        { label: 'Style', value: 'Photoreal' },
        { label: 'Quality', value: '4K' },
      ],
      title: 'AI rendering',
      version: 'v3 · 4K · Day',
    },
    renderAbility: {
      body: 'Drop the screenshot, choose time of day, style, and quality up to 4K. No V-Ray, no queue, no blowing the margin.',
      chips: ['Day', 'Night', 'Dusk', 'Photoreal', 'Conceptual', '1K', '4K'],
      tag: 'RENDERING',
      title: 'A SketchUp screenshot becomes a rendering in seconds',
    },
    summary: {
      body: 'In one sentence, out comes the summary of a whole week on site. Decisions, open items, and what changed.',
      miniLabel: 'WEEK IN SUMMARY',
      stats: [
        { l: 'tasks', v: '12' },
        { l: 'decisions', v: '3' },
        { l: 'risk', v: '1' },
      ],
      tag: 'SUMMARY',
      title: 'Tells you what moved on a project',
    },
  },
  'pt-BR': {
    abilitiesIntro: 'Quatro habilidades que mudam o ritmo do escritório',
    assistant: {
      addLabel: 'Adicionar ao projeto',
      bubbles: [
        { mine: true, text: 'Faça um resumo do que andou na Residência Lopes nesta semana.' },
        {
          mine: false,
          text: 'Marina fechou o detalhamento da marcenaria, a hidráulica foi compatibilizada e ficou pendente a aprovação do projeto elétrico. A obra está 64% do escopo entregue.',
        },
        { mine: true, text: 'Rascunhe o termo de aprovação dessa etapa.' },
      ],
      context: 'Com contexto da Residência Lopes',
      draftLabel: '✦ TERMO DE APROVAÇÃO, RASCUNHO',
      editLabel: 'Editar',
      meta: '132 palavras · 3 fontes',
      name: 'IA do Planno',
      sources: '7 fontes do projeto',
    },
    contract: {
      body: 'Submete contrato, aditivo ou termo contra o checklist jurídico do escritório. Sinaliza o que precisa revisar.',
      note: '✦ 2 itens precisam de revisão',
      rows: [
        { icon: '✓', label: 'Cláusula de rescisão', tone: '#7bb86b' },
        { icon: '✓', label: 'Foro eleito · cidade', tone: '#7bb86b' },
        { icon: '!', label: 'Propriedade intelectual', tone: '#e8a13d' },
        { icon: '✕', label: 'Cronograma + pagamento', tone: '#e0593f' },
      ],
      tag: 'REVISÃO',
      title: 'Lê contratos com a sua régua',
    },
    eyebrow: 'INTELIGÊNCIA',
    heading: 'A IA que já leu tudo do seu projeto',
    intro:
      'O assistente conhece os arquivos, as conversas, o orçamento e o cronograma. Pergunte, peça um rascunho ou gere um render. Tudo dentro do mesmo projeto.',
    proposal: {
      body: 'Briefing preenchido vira proposta com escopo, etapas, parcelas e valor, pronta para você revisar.',
      sourceLabel: 'Briefing, Mariana Vieira',
      suggestedLabel: 'PROPOSTA SUGERIDA',
      tag: 'GERAÇÃO',
      terms: '5 etapas · 6× R$ 6.400',
      title: 'Briefing vira proposta',
      value: 'R$ 48.000',
    },
    render: {
      beforeLabel: 'ANTES · SKETCHUP',
      speedBadge: '2,8s · grátis',
      stats: [
        { label: 'Hora', value: 'Dia · 14h' },
        { label: 'Estilo', value: 'Fotorrealista' },
        { label: 'Qualidade', value: '4K' },
      ],
      title: 'Render IA',
      version: 'v3 · 4K · Dia',
    },
    renderAbility: {
      body: 'Solte o screenshot, escolha hora do dia, estilo e qualidade até 4K. Sem V-Ray, sem fila, sem estourar a margem.',
      chips: ['Dia', 'Noite', 'Crepúsculo', 'Fotorrealista', 'Conceitual', '1K', '4K'],
      tag: 'RENDER',
      title: 'Print do SketchUp vira render em segundos',
    },
    summary: {
      body: 'Em uma frase, sai o resumo de uma semana inteira de obra. Decisões, pendências e o que mudou.',
      miniLabel: 'RESUMO DA SEMANA',
      stats: [
        { l: 'tarefas', v: '12' },
        { l: 'decisões', v: '3' },
        { l: 'risco', v: '1' },
      ],
      tag: 'RESUMO',
      title: 'Conta o que andou em um projeto',
    },
  },
}

const AssistantHero = ({ content }: { content: AiContent }) => (
  <div className="overflow-hidden rounded-2xl border border-white/10 bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
    <div className="flex items-center gap-2 border-[#f1f1f1] border-b bg-gradient-to-br from-[#f6f4fc] to-white px-5 py-3">
      <span className="flex size-7 items-center justify-center rounded-md bg-[#7a6cb6] text-white">
        <SparkleIcon className="size-3.5" />
      </span>
      <div>
        <p className="font-semibold text-[#1c1c20] text-[12.5px]">{content.assistant.name}</p>
        <p className="text-[#9a9aa0] text-[10.5px]">{content.assistant.context}</p>
      </div>
      <span className="ml-auto rounded-md bg-[#f1eaff] px-2 py-1 text-[#7a6cb6] text-[10px]">
        {content.assistant.sources}
      </span>
    </div>
    <div className="space-y-3 px-5 py-5">
      {content.assistant.bubbles.map((m, i) => (
        <div className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`} key={i}>
          <div
            className="max-w-[78%] rounded-2xl px-3.5 py-2.5 text-[12.5px] leading-relaxed"
            style={{
              background: m.mine ? '#7a6cb6' : '#f4f0fb',
              color: m.mine ? '#fff' : '#1c1c20',
            }}
          >
            {m.text}
          </div>
        </div>
      ))}
      <div className="rounded-xl border border-[#e7e3f4] bg-white p-3.5">
        <p className="text-[#7a6cb6] text-[10px] tracking-[0.16em]">
          {content.assistant.draftLabel}
        </p>
        <div className="mt-2 space-y-1.5">
          <span className="block h-[7px] w-[92%] rounded-full bg-[#e7e3f4]" />
          <span className="block h-[7px] w-[100%] rounded-full bg-[#e7e3f4]" />
          <span className="block h-[7px] w-[100%] rounded-full bg-[#e7e3f4]" />
          <span className="block h-[7px] w-[68%] rounded-full bg-[#e7e3f4]" />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className="rounded-md bg-[#1d1d20] px-2.5 py-1.5 font-medium text-[10.5px] text-white">
            {content.assistant.addLabel}
          </span>
          <span className="rounded-md border border-[#ececec] px-2.5 py-1.5 text-[#43434a] text-[10.5px]">
            {content.assistant.editLabel}
          </span>
          <span className="ml-auto text-[#9a9aa0] text-[10px]">{content.assistant.meta}</span>
        </div>
      </div>
    </div>
  </div>
)

const RenderCompare = ({ content }: { content: AiContent }) => (
  <div className="overflow-hidden rounded-2xl border border-white/20 bg-[#0b0b0d] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
    <div className="flex items-center gap-2 border-white/10 border-b px-4 py-2.5">
      <span className="flex size-6 items-center justify-center rounded-md bg-[#d96fa3] text-white">
        <SparkleIcon className="size-3" />
      </span>
      <p className="font-semibold text-[12px] text-white">{content.render.title}</p>
      <span className="ml-auto text-[10px] text-white/60">{content.render.version}</span>
    </div>
    <div className="relative">
      <div
        className="aspect-[16/10]"
        style={{
          background:
            'linear-gradient(135deg, #d4a78a 0%, #e8c4a8 25%, #b89b8a 50%, #5f5246 100%), repeating-linear-gradient(45deg, rgba(0,0,0,0.03) 0 8px, transparent 8px 16px)',
        }}
      />
      {/* "Before" preview overlapping the bottom-left corner */}
      <div className="absolute bottom-3 left-3 w-[42%] rounded-md border-2 border-white/40 bg-[#2c2c34] p-2 shadow-lg">
        <p className="text-[8.5px] text-white/60 tracking-wide">{content.render.beforeLabel}</p>
        <div className="mt-1 grid grid-cols-4 gap-0.5">
          {Array.from({ length: 12 }).map((_, i) => (
            <span className="block aspect-square rounded-sm bg-white/10" key={i} />
          ))}
        </div>
      </div>
      <div className="absolute top-3 right-3 rounded-full bg-white/90 px-3 py-1 font-semibold text-[#1c1c20] text-[10px]">
        {content.render.speedBadge}
      </div>
    </div>
    <div className="grid grid-cols-3 gap-2 px-3 py-3">
      {content.render.stats.map((s) => (
        <div className="rounded-md bg-white/5 px-2.5 py-1.5" key={s.label}>
          <p className="text-[9px] text-white/50">{s.label}</p>
          <p className="font-semibold text-[10.5px] text-white">{s.value}</p>
        </div>
      ))}
    </div>
  </div>
)

const Ability = ({
  tag,
  title,
  body,
  children,
  heading: CardHeading = 'h3',
}: {
  tag: string
  title: string
  body: string
  children: React.ReactNode
  /** One level below the section heading: `h2` when the section renders `h1`. */
  heading?: 'h2' | 'h3'
}) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-colors hover:bg-white/[0.06]">
    <p className="text-[#d96fa3] text-[10.5px] tracking-[0.2em]">{tag}</p>
    <CardHeading className="mt-2 font-semibold text-[18px] text-white leading-snug">
      {title}
    </CardHeading>
    <p className="mt-2 text-[#a8a3b4] text-[13.5px] leading-relaxed">{body}</p>
    <div className="mt-5">{children}</div>
  </div>
)

const SummaryMini = ({ content }: { content: AiContent }) => (
  <div className="rounded-xl border border-white/10 bg-[#0b0b0d] p-4">
    <p className="text-[#a8a3b4] text-[10px] tracking-[0.16em]">{content.summary.miniLabel}</p>
    <div className="mt-2 space-y-1.5">
      <span className="block h-[6px] w-[94%] rounded-full bg-white/15" />
      <span className="block h-[6px] w-[100%] rounded-full bg-white/15" />
      <span className="block h-[6px] w-[72%] rounded-full bg-white/15" />
    </div>
    <div className="mt-3 grid grid-cols-3 gap-2 text-center">
      {content.summary.stats.map((s) => (
        <div className="rounded-md bg-white/[0.04] py-1.5" key={s.l}>
          <p className="font-semibold text-[14px] text-white">{s.v}</p>
          <p className="text-[9px] text-white/50">{s.l}</p>
        </div>
      ))}
    </div>
  </div>
)

const ContractMini = ({ content }: { content: AiContent }) => (
  <div className="rounded-xl border border-white/10 bg-[#0b0b0d] p-4">
    {content.contract.rows.map((row) => (
      <div
        className="flex items-center gap-2 border-white/5 border-t py-1.5 first:border-t-0 first:pt-0"
        key={row.label}
      >
        <span
          className="flex size-[14px] items-center justify-center rounded-[3px] font-bold text-[8px] text-white"
          style={{ background: row.tone }}
        >
          {row.icon}
        </span>
        <span className="text-[11px] text-white">{row.label}</span>
      </div>
    ))}
    <p className="mt-2 text-[#d96fa3] text-[9.5px] tracking-[0.16em]">{content.contract.note}</p>
  </div>
)

const ProposalMini = ({ content }: { content: AiContent }) => (
  <div className="rounded-xl border border-white/10 bg-[#0b0b0d] p-4">
    <div className="flex items-center gap-2 rounded-lg bg-white/[0.04] p-2.5">
      <span className="flex size-6 items-center justify-center rounded-md bg-[#fde9b8] text-[#a87a18] text-[11px]">
        ⤓
      </span>
      <span className="text-[11px] text-white">{content.proposal.sourceLabel}</span>
    </div>
    <p className="mt-2 text-center text-[#a8a3b4] text-[18px]">↓</p>
    <div className="rounded-lg bg-gradient-to-br from-[#7a6cb6] to-[#5b3d8e] p-3">
      <p className="text-[9px] text-white/70 tracking-[0.16em]">
        {content.proposal.suggestedLabel}
      </p>
      <p className="mt-1 font-bold text-[16px] text-white">{content.proposal.value}</p>
      <p className="text-[10px] text-white/80">{content.proposal.terms}</p>
    </div>
  </div>
)

export const AiSection = ({
  headingLevel: Heading = 'h2',
}: {
  /** `h1` when the section opens a deep-dive page and owns its top heading. */
  headingLevel?: 'h1' | 'h2'
}) => {
  const content = useLocalized(CONTENT)
  // Sub-headings sit one level below the section heading so the document
  // outline never skips a level (h1 → h2 on the AI page, h2 → h3 elsewhere).
  const SubHeading = Heading === 'h1' ? 'h2' : 'h3'

  return (
    <section className="relative overflow-hidden bg-[#0b0b0d] py-32 text-white">
      <TruchetField
        cell={56}
        className="absolute inset-0 h-full w-full text-white opacity-[0.06]"
        seed="ia"
        variant="multiscale"
      />
      {/* Soft purple glow behind the hero */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[480px] opacity-50">
        <div className="absolute top-[10%] left-1/2 size-[640px] -translate-x-1/2 rounded-full bg-[#7a6cb6] blur-[140px]" />
        <div className="absolute top-[30%] right-[10%] size-[420px] rounded-full bg-[#d96fa3] blur-[140px]" />
      </div>

      <div className="relative mx-auto max-w-[1120px] px-6">
        <ScrollReveal className="text-center">
          <p className="text-[#d96fa3] text-[12px] tracking-[0.2em]">{content.eyebrow}</p>
          <Heading className="mx-auto mt-3 max-w-[880px] font-display text-[clamp(40px,6vw,64px)] leading-[1.02] tracking-[-0.01em]">
            {content.heading}
          </Heading>
          <p className="mx-auto mt-5 max-w-[640px] text-[#a8a3b4] text-[clamp(16px,2.2vw,20px)]">
            {content.intro}
          </p>
        </ScrollReveal>

        {/* Hero, overlapping chat and render comparison */}
        <div className="relative mt-16 grid gap-6 md:grid-cols-[1.25fr_1fr] md:gap-0">
          <ScrollReveal className="md:pr-[60px]">
            <AssistantHero content={content} />
          </ScrollReveal>
          <ScrollReveal className="md:mt-16 md:-ml-[80px]" delay={140}>
            <RenderCompare content={content} />
          </ScrollReveal>
        </div>

        {/* Abilities, each with its own real UI surface, not just a bullet list */}
        <div className="mt-20">
          <ScrollReveal className="text-center">
            <p className="text-[#a8a3b4] text-[13px]">{content.abilitiesIntro}</p>
          </ScrollReveal>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            <ScrollReveal>
              <Ability
                body={content.summary.body}
                heading={SubHeading}
                tag={content.summary.tag}
                title={content.summary.title}
              >
                <SummaryMini content={content} />
              </Ability>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <Ability
                body={content.contract.body}
                heading={SubHeading}
                tag={content.contract.tag}
                title={content.contract.title}
              >
                <ContractMini content={content} />
              </Ability>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <Ability
                body={content.proposal.body}
                heading={SubHeading}
                tag={content.proposal.tag}
                title={content.proposal.title}
              >
                <ProposalMini content={content} />
              </Ability>
            </ScrollReveal>
          </div>

          {/* Final wide ability: render gallery, different layout from the three above */}
          <ScrollReveal className="mt-5" delay={300}>
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04]">
              <div className="grid items-stretch gap-0 md:grid-cols-[1fr_1.6fr]">
                <div className="p-7">
                  <p className="text-[#d96fa3] text-[10.5px] tracking-[0.2em]">
                    {content.renderAbility.tag}
                  </p>
                  <SubHeading className="mt-2 font-semibold text-[20px] text-white leading-snug">
                    {content.renderAbility.title}
                  </SubHeading>
                  <p className="mt-3 text-[#a8a3b4] text-[13.5px] leading-relaxed">
                    {content.renderAbility.body}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-1.5">
                    {content.renderAbility.chips.map((chip) => (
                      <span
                        className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] text-white/80"
                        key={chip}
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-1 p-3">
                  {[
                    'linear-gradient(135deg, #d4a78a 0%, #e8c4a8 50%, #5f5246 100%)',
                    'linear-gradient(135deg, #1a2b4a 0%, #d96fa3 50%, #f6cf5b 100%)',
                    'linear-gradient(135deg, #9fc3e8 0%, #c4d9a8 50%, #7fae8a 100%)',
                    'linear-gradient(135deg, #2c2c34 0%, #6f63d6 60%, #f6cfe0 100%)',
                    'linear-gradient(135deg, #f6cfc6 0%, #e8a13d 60%, #c4502f 100%)',
                    'linear-gradient(135deg, #1d7a8c 0%, #4fa395 50%, #cfe7e2 100%)',
                  ].map((bg, i) => (
                    <div className="aspect-square rounded-md" key={i} style={{ background: bg }} />
                  ))}
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
